import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const chantierSchema = z.object({
  contact_id: z.string().optional(),
  devis_id: z.string().optional(),
  adresse_chantier: z.string().optional(),
  ville: z.string().optional(),
  statut: z.enum(['Prévu', 'En cours', 'Terminé', 'Bloqué']).optional(),
  description_travaux: z.string().optional(),
  budget_prevu: z.number().optional(),
  montant_total_devis: z.number().optional(),
  responsable_interne: z.string().optional(),
  photos_avant_urls: z.array(z.string()).optional(),
  photos_pendant_urls: z.array(z.string()).optional(),
  photos_apres_urls: z.array(z.string()).optional(),
});

export default function ChantierForm({ chantier, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [devisList, setDevisList] = useState([]);
  const [sousTraitants, setSousTraitants] = useState([]);
  const [selectedST, setSelectedST] = useState([]);
  const [dateDebut, setDateDebut] = useState(chantier?.date_debut ? new Date(chantier.date_debut) : null);
  const [dateFin, setDateFin] = useState(chantier?.date_fin_estimee ? new Date(chantier.date_fin_estimee) : null);
  const [dateLimite, setDateLimite] = useState(chantier?.date_limite ? new Date(chantier.date_limite) : null);

  useEffect(() => {
    fetchContacts();
    fetchDevis();
    fetchSousTraitants();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // Réutilise l'endpoint agenda qui expose les users actifs sans exiger admin
      const r = await api.get('/agenda/assignable-users').catch(() => ({ data: [] }));
      setUsers(Array.isArray(r.data) ? r.data : []);
    } catch (e) { /* silent */ }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    }
  };

  const fetchDevis = async () => {
    try {
      const response = await api.get('/devis');
      setDevisList(response.data);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    }
  };

  const fetchSousTraitants = async () => {
    try {
      const response = await api.get('/sous-traitants');
      setSousTraitants(response.data);
    } catch (error) {
      console.error('Erreur chargement sous-traitants:', error);
    }
  };

  const toggleSousTraitant = (stId) => {
    setSelectedST(prev =>
      prev.includes(stId) ? prev.filter(id => id !== stId) : [...prev, stId]
    );
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(chantierSchema),
    defaultValues: chantier || {
      statut: 'Prévu',
    },
  });

  const contactIdValue = watch('contact_id');
  const devisIdValue = watch('devis_id');
  const statutValue = watch('statut');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        date_debut: dateDebut ? dateDebut.toISOString() : null,
        date_fin_estimee: dateFin ? dateFin.toISOString() : null,
        date_limite: dateLimite ? dateLimite.toISOString() : null,
      };
      
      let chantierId;
      if (chantier) {
        await api.put(`/chantiers/${chantier.chantier_id}`, payload);
        chantierId = chantier.chantier_id;
      } else {
        const response = await api.post('/chantiers', payload);
        chantierId = response.data.chantier_id;
      }

      // Créer les affectations pour les sous-traitants sélectionnés
      if (selectedST.length > 0 && chantierId) {
        for (const stId of selectedST) {
          try {
            await api.post('/affectations', {
              chantier_id: chantierId,
              sous_traitant_id: stId,
              mission: 'À définir',
              statut: 'Prévu'
            });
          } catch (err) {
            console.error('Erreur création affectation:', err);
          }
        }
      }
      
      onSuccess();
    } catch (error) {
      console.error('Erreur sauvegarde chantier:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contact_id">Contact <span className="text-slate-400 text-xs font-normal">(optionnel)</span></Label>
          <Select value={contactIdValue || 'none'} onValueChange={(value) => setValue('contact_id', value === 'none' ? '' : value)}>
            <SelectTrigger data-testid="chantier-contact-select">
              <SelectValue placeholder="Aucun contact" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Aucun —</SelectItem>
              {contacts.map((contact) => (
                <SelectItem key={contact.contact_id} value={contact.contact_id}>
                  {contact.nom_prenom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.contact_id && <p className="text-sm text-red-600 mt-1">{errors.contact_id.message}</p>}
        </div>

        <div>
          <Label htmlFor="devis_id">Devis Associé</Label>
          <Select value={devisIdValue || 'none'} onValueChange={(value) => setValue('devis_id', value === 'none' ? '' : value)}>
            <SelectTrigger data-testid="chantier-devis-select">
              <SelectValue placeholder="Optionnel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {devisList.map((devis) => (
                <SelectItem key={devis.devis_id} value={devis.devis_id}>
                  {devis.devis_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="adresse_chantier">Adresse du Chantier *</Label>
        <Input id="adresse_chantier" {...register('adresse_chantier')} data-testid="chantier-adresse-input" />
        {errors.adresse_chantier && <p className="text-sm text-red-600 mt-1">{errors.adresse_chantier.message}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ville">Ville *</Label>
          <Input id="ville" {...register('ville')} data-testid="chantier-ville-input" />
          {errors.ville && <p className="text-sm text-red-600 mt-1">{errors.ville.message}</p>}
        </div>

        <div>
          <Label htmlFor="statut">Statut *</Label>
          <Select value={statutValue} onValueChange={(value) => setValue('statut', value)}>
            <SelectTrigger data-testid="chantier-statut-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Prévu">Prévu</SelectItem>
              <SelectItem value="En cours">En cours</SelectItem>
              <SelectItem value="Terminé">Terminé</SelectItem>
              <SelectItem value="Bloqué">Bloqué</SelectItem>
            </SelectContent>
          </Select>
          {errors.statut && <p className="text-sm text-red-600 mt-1">{errors.statut.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Date Début</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateDebut ? format(dateDebut, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={dateDebut} onSelect={setDateDebut} locale={fr} />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label>Date Fin Estimée</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateFin ? format(dateFin, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={dateFin} onSelect={setDateFin} locale={fr} />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div>
        <Label>Date Limite ⚠️</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal border-red-200">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateLimite ? format(dateLimite, 'dd/MM/yyyy', { locale: fr }) : 'Date limite de fin'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={dateLimite} onSelect={setDateLimite} locale={fr} />
          </PopoverContent>
        </Popover>
        <p className="text-xs text-slate-500 mt-1">Date butoir impérative pour la fin du chantier</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="budget_prevu">Budget Prévu (€)</Label>
          <Input
            id="budget_prevu"
            type="number"
            step="0.01"
            {...register('budget_prevu', { valueAsNumber: true })}
            data-testid="chantier-budget-prevu-input"
          />
        </div>

        <div>
          <Label htmlFor="montant_total_devis">Montant Total Devis (€)</Label>
          <Input
            id="montant_total_devis"
            type="number"
            step="0.01"
            {...register('montant_total_devis', { valueAsNumber: true })}
            placeholder="Rempli auto depuis devis"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="responsable_interne">Responsable interne</Label>
        <Select
          value={watch('responsable_interne') || 'none'}
          onValueChange={(value) => setValue('responsable_interne', value === 'none' ? '' : value)}
        >
          <SelectTrigger data-testid="chantier-responsable-select">
            <SelectValue placeholder="Sélectionner un collaborateur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Aucun —</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.user_id} value={u.name || u.email}>
                {u.name || u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="description_travaux">Description des Travaux</Label>
        <Textarea id="description_travaux" {...register('description_travaux')} rows={4} />
      </div>

      {/* Sélection multiple sous-traitants */}
      <div>
        <Label>Sous-traitants</Label>
        <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
          {sousTraitants.length > 0 ? (
            sousTraitants.map((st) => (
              <div
                key={st.sous_traitant_id}
                onClick={() => toggleSousTraitant(st.sous_traitant_id)}
                className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                  selectedST.includes(st.sous_traitant_id)
                    ? 'bg-blue-50 border border-blue-200'
                    : 'hover:bg-slate-50 border border-transparent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedST.includes(st.sous_traitant_id)}
                    onChange={() => {}}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium text-sm">{st.nom}</p>
                    <p className="text-xs text-slate-500">{st.specialite}</p>
                  </div>
                </div>
                {st.prix_range && (
                  <span className="text-amber-600 font-medium text-sm">{st.prix_range}</span>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">Aucun sous-traitant disponible</p>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">{selectedST.length} sous-traitant(s) sélectionné(s)</p>
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} data-testid="submit-chantier-btn">
          {loading ? 'Enregistrement...' : chantier ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}